// List Directory Tool - Provides directory listing functions for workspace
// Extends WorkspaceTool - has access to appType and toolSystem

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class ListDirectoryTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'listDirectoryTool';
  }
  /**
   * List directory contents
   * @param {string} path - Path to the directory to list
   * @param {boolean} [recursive=false] - List directories recursively
   * @param {boolean} [includeStats=false] - Include file/directory stats
   * @param {boolean} [includeHidden=false] - Include hidden files (starting with .)
   * @returns {Promise<Object>} Result with directory entries
   */
  async listDirectory(
    path,
    recursive = false,
    includeStats = false,
    includeHidden = false
  ) {
    try {
      // Validate path
      if (!path || typeof path !== "string") {
        return this.returnError({}, "Valid directory path required");
      }

      // Route based on app type
      if (this.appType === "browser") {
        return await this.listDirectoryForBrowser(
          path,
          recursive,
          includeStats,
          includeHidden
        );
      }
      if (this.appType === "local") {
        return await this.listDirectoryForLocal(
          path,
          recursive,
          includeStats,
          includeHidden
        );
      }
      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for listDirectory`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|listdir|error",
          color1: "red",
        },
        `Failed to list directory: ${path}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * List directory for browser apps using Lightning FS
   */
  async listDirectoryForBrowser(path, recursive, includeStats, includeHidden) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    // Normalize path - Lightning FS uses absolute paths
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    // Prepend /files/ to the filesystem path
    const filesystemPath = `/files${normalizedPath}`;

    try {
      // Check if path exists and is a directory
      const stats = await fs.stat(filesystemPath);
      const isDir =
        typeof stats.isDirectory === "function"
          ? stats.isDirectory()
          : stats.type === "dir";

      if (!isDir) {
        return this.returnError({}, `Path is not a directory: ${path}`);
      }

      // List directory contents
      const entries = await this.listDirectoryRecursive(
        filesystemPath,
        normalizedPath,
        recursive,
        includeStats,
        includeHidden
      );

      logger.log(
        {
          tags: "tools|listdir|info",
          color1: "lime",
        },
        `Listed directory: ${filesystemPath}`,
        {
          entryCount: entries.length,
          recursive,
          includeStats,
        }
      );

      return this.returnSuccess(
        {
          path: normalizedPath, // Return the original path without /files/ prefix
          entries,
          count: entries.length,
        },
        `Directory listed successfully with ${entries.length} entry(ies)`
      );
    } catch (error) {
      if (error.code === "ENOENT") {
        return this.returnError({}, `Directory not found: ${path}`);
      }
      throw error;
    }
  }

  /**
   * List directory for local apps (Revolt Desktop + workspaceLocalAppAPI).
   */
  async listDirectoryForLocal(path, recursive, includeStats, includeHidden) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.listDirectory) {
      return this.returnError(
        {},
        "Local directory listing requires Revolt Desktop (workspaceLocalAppAPI.listDirectory)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    let relativePath = normalizedPath.replace(/^\/+/, "");
    if (relativePath === "") {
      relativePath = ".";
    }

    const res = await api.listDirectory({
      rootPath: root,
      relativePath,
      recursive,
      includeStats,
      includeHidden,
    });

    if (!res?.success) {
      return this.returnError({}, res?.error || "Failed to list directory on disk");
    }

    const displayPath = normalizedPath;

    logger.log(
      {
        tags: "tools|listdir|info",
        color1: "lime",
      },
      `Listed directory (local): ${displayPath}`,
      {
        entryCount: res.count,
        recursive,
        includeStats,
      }
    );

    return this.returnSuccess(
      {
        path: displayPath,
        entries: res.entries || [],
        count: res.count ?? (res.entries?.length || 0),
      },
      `Directory listed successfully with ${res.count ?? res.entries?.length ?? 0} entry(ies)`
    );
  }

  /**
   * Recursively list directory contents
   * @param {string} filesystemPath - Full filesystem path (with /files/ prefix)
   * @param {string} relativePath - Relative path from workspace root (without /files/ prefix)
   * @param {boolean} recursive - Whether to list recursively
   * @param {boolean} includeStats - Whether to include file stats
   * @param {boolean} includeHidden - Whether to include hidden files
   * @returns {Promise<Array>} Array of directory entries
   */
  async listDirectoryRecursive(
    filesystemPath,
    relativePath,
    recursive,
    includeStats,
    includeHidden
  ) {
    const entries = [];

    try {
      const items = await this.fs.promises.readdir(filesystemPath);

      for (const item of items) {
        // Skip hidden files unless requested
        if (!includeHidden && item.startsWith(".")) {
          continue;
        }

        const itemFilesystemPath =
          filesystemPath === "/files"
            ? `/files/${item}`
            : `${filesystemPath}/${item}`;
        const itemRelativePath =
          relativePath === "/" ? `/${item}` : `${relativePath}/${item}`;

        try {
          const stats = await this.fs.promises.stat(itemFilesystemPath);
          const isDir =
            typeof stats.isDirectory === "function"
              ? stats.isDirectory()
              : stats.type === "dir";

          const entry = {
            name: item,
            path: itemRelativePath,
            type: isDir ? "directory" : "file",
          };

          // Add stats if requested
          if (includeStats) {
            entry.size = stats.size || 0;
            entry.modified = stats.mtime || null;
            entry.created = stats.ctime || null;
          }

          // Add file extension for files
          if (!isDir) {
            const extension = this.getFileExtension(item);
            if (extension) {
              entry.extension = extension;
            }
          }

          entries.push(entry);

          // Recursively list subdirectories
          if (recursive && isDir) {
            const subEntries = await this.listDirectoryRecursive(
              itemFilesystemPath,
              itemRelativePath,
              true,
              includeStats,
              includeHidden
            );
            entries.push(...subEntries);
          }
        } catch (error) {
          logger.warn(
            {
              tags: "tools|listdir|entry",
              color1: "yellow",
            },
            `Failed to stat entry: ${itemFilesystemPath}`,
            error
          );
          // Continue with other entries even if one fails
        }
      }
    } catch (error) {
      logger.error(
        {
          tags: "tools|listdir|read",
          color1: "red",
        },
        `Failed to read directory: ${filesystemPath}`,
        error
      );
      throw error;
    }

    // Sort entries: directories first, then alphabetically by name
    return entries.sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === "directory" ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
  }

  /**
   * Get file extension from filename
   * @param {string} name - Filename
   * @returns {string} File extension (lowercase) or empty string
   */
  getFileExtension(name = "") {
    const lastDot = name.lastIndexOf(".");
    if (lastDot === -1 || lastDot === name.length - 1) {
      return "";
    }
    return name.slice(lastDot + 1).toLowerCase();
  }
}
